
import { RECURRENCE_UNIT_SHORT } from '../../utils/ruleRecurrence';
import { countRu, CAMERA_FORMS, EVENT_FORMS, VIOLATION_FORMS } from '../../utils/pluralRu';

/*
 * Форматирование response Executive Summary → UI. Только представление:
 * никаких пересчётов KPI/тренда/heatmap (ТЗ №1 §6, ключевой принцип).
 */

const num1 = (v) => (v == null || !Number.isFinite(Number(v)) ? '—' : Number(v).toFixed(1).replace('.', ','));
const pct0 = (v) => (v == null || !Number.isFinite(Number(v)) ? '—' : `${Math.round(Math.abs(Number(v)))}%`);

/**
 * Изменение числа нарушений к прошлому периоду — одна строка под KPI.
 * Направление передаётся знаком и цветом, без стрелок (docs/STYLE_GUIDE.md).
 *
 * Без истории сравнивать не с чем: backend при no_prior_history всё равно
 * отдаёт direction = new и previous = 0, и «Новые» выглядело бы как рост.
 */
export const describeChange = (
  direction, percent, comparisonState = 'available', newText = 'В прошлом периоде нарушений не было'
) => {
  if (comparisonState !== 'available') return null;
  if (direction === 'new') return { label: newText, tone: 'new' };
  if (direction === 'same') return { label: 'Как в прошлом периоде', tone: 'same' };
  const sign = direction === 'down' ? '−' : '+';
  return {
    label: `${sign}${pct0(percent)} к прошлому периоду`,
    tone: direction === 'down' ? 'down' : 'up',
  };
};

/** null — «Недостаточно данных», не 0 (ТЗ №1 §12.2). */
export const formatRate = (value) => (value == null ? 'Недостаточно данных' : num1(value));
export const formatPercent = (value) => (value == null ? 'Недостаточно данных' : `${num1(value)}%`);

/** Часы контроля/простоя: единый формат для всех аналитических таблиц. */
export const formatHours = (value) => (value == null ? 'Недостаточно данных' : `${num1(value)} ч`);

/** Ячейка таблицы: нет значения — «—», а не фраза в числовой колонке. */
export const formatRateCell = (value) => (value == null ? '—' : num1(value));
export const formatPercentCell = (value) => (value == null ? '—' : `${num1(value)}%`);
export const formatHoursCell = (value) => (value == null ? '—' : `${num1(value)} ч`);

export const WEEKDAY_LABELS = ['', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

/*
 * ANALYTICS-OPS-INSIGHTS01 (ТЗ v4 §34, FE-FIX31): распределения считает
 * backend. Здесь только подписи: минуты суток → время. Никакого пересчёта
 * долей, объединения или разбиения интервалов —
 * границы, доли и «другие интервалы» приходят готовыми.
 */

/** 0 → «00:00», 1440 → «24:00» (конец суток backend отдаёт именно так). */
export const formatMinuteOfDay = (minute) => {
  if (minute == null || minute === '') return '—';
  const value = Number(minute);
  if (!Number.isFinite(value)) return '—';
  const hours = Math.floor(value / 60);
  const minutes = value % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

export const describeTimeInterval = (interval) =>
  `${formatMinuteOfDay(interval?.start_minute)}–${formatMinuteOfDay(interval?.end_minute)}`;

/** Фиксированный порядок Пн–Вс; отсутствующий в ответе день — item: null, не ноль. */
export const orderWeekdayDistribution = (items) => {
  const byWeekday = new Map(
    (Array.isArray(items) ? items : []).map((item) => [Number(item?.weekday), item])
  );
  return [1, 2, 3, 4, 5, 6, 7].map((weekday) => ({
    weekday,
    label: WEEKDAY_LABELS[weekday],
    item: byWeekday.get(weekday) || null,
  }));
};

/**
 * Публичные recurrence-поля паттерна (ТЗ v4 §34). Окно показываем, только
 * когда оно задано правилом: для default_context_interval конкретного
 * интервала у фронта нет — тогда окно просто не упоминается.
 */
export const describePatternRecurrence = (pattern) => {
  const threshold = pattern?.recurrence_threshold_count;
  if (threshold == null) return null;
  const matched = pattern?.recurrence_match_count ?? 0;
  const base = `${countRu(matched, ['повтор', 'повтора', 'повторов'])} при пороге ${threshold}`;
  const value = pattern?.recurrence_window_value;
  const unit = pattern?.recurrence_window_unit;
  if (pattern?.recurrence_source === 'rule' && value != null && unit) {
    return `${base} за ${value} ${RECURRENCE_UNIT_SHORT[unit] || unit}`;
  }
  return base;
};

export const heatmapHasData = (cells, countKey = 'violations_count') =>
  (Array.isArray(cells) ? cells : []).some((cell) => Number(cell?.[countKey]) > 0);

/** ТЗ №1 §9.2: backend отдаёт только ненулевые cells; сетка 7×24, weekday 1=Пн. */
export const buildHeatmapGrid = (cells, countKey = 'violations_count') => {
  const grid = Array.from({ length: 7 }, () => Array(24).fill(0));
  (Array.isArray(cells) ? cells : []).forEach((cell) => {
    const w = Number(cell?.weekday);
    const h = Number(cell?.hour);
    if (w >= 1 && w <= 7 && h >= 0 && h <= 23) grid[w - 1][h] = Number(cell[countKey]) || 0;
  });
  return grid;
};

export const heatmapLevel = (count) => {
  if (!count) return 0;
  if (count <= 2) return 1;
  if (count <= 5) return 2;
  if (count <= 10) return 3;
  return 4;
};

/**
 * Дополнение к ТЗ №1 §3 / ТЗ №2 §10.2. Подпись строго по backend timezone
 * metadata; при none/отсутствии — нейтральная строка без выдуманной зоны.
 */
export const describeTimezoneScope = (scope) => {
  const mode = scope?.timezone_mode;
  if (mode === 'single' && scope.display_timezone) {
    return { text: `Часовой пояс ${scope.display_timezone}`, title: '' };
  }
  if (mode === 'per_camera') {
    const zones = Array.isArray(scope.display_timezones) ? scope.display_timezones.join(', ') : '';
    return {
      text: 'Время показано в локальном часовом поясе каждой камеры',
      title: zones ? `Часовые пояса камер: ${zones}` : '',
    };
  }
  return { text: '', title: '' };
};

export const INSIGHT_SEVERITY_CLASS = {
  critical: 'sum-insight--critical',
  attention: 'sum-insight--attention',
  observe: 'sum-insight--observe',
  positive: 'sum-insight--positive',
};

const RATE_CHANGE_CODES = new Set(['violation_rate_worsened', 'violation_rate_improved']);

/**
 * Сравнение rate без прошлого периода или с нулевым прошлым rate — не
 * ухудшение, а отсутствие базы: backend такие сигналы отдаёт, фронт прячет.
 */
export const filterInsights = (items, comparisonState = 'available') =>
  (Array.isArray(items) ? items : []).filter((insight) => {
    if (!RATE_CHANGE_CODES.has(insight?.code)) return true;
    const facts = insight.facts || {};
    const percent = insight.code === 'violation_rate_worsened' ? facts.change_percent : facts.improvement_percent;
    return comparisonState === 'available'
      && Number(facts.previous_rate) > 0
      && percent != null
      && Number.isFinite(Number(percent));
  });

/** ТЗ №1 §8.2: facts форматируются по known code; title/explanation — backend. */
export const formatInsightFacts = (code, facts, cameraNames = {}) => {
  if (!facts || typeof facts !== 'object') return '';
  switch (code) {
    case 'violation_rate_worsened':
      return `Было ${num1(facts.previous_rate)}, стало ${num1(facts.current_rate)} на 100 ч · +${pct0(facts.change_percent)}`;
    case 'violation_rate_improved':
      return `Было ${num1(facts.previous_rate)}, стало ${num1(facts.current_rate)} на 100 ч · −${pct0(facts.improvement_percent)}`;
    case 'repeated_violation_pattern':
      return facts.violations_count != null ? countRu(facts.violations_count, EVENT_FORMS) : '';
    case 'time_concentration': {
      const day = WEEKDAY_LABELS[Number(facts.weekday)] || '—';
      const hour = facts.hour != null ? `${String(facts.hour).padStart(2, '0')}:00` : '—';
      return `${day}, ${hour} · ${pct0(facts.share_percent)} нарушений · локальное время камер`;
    }
    case 'no_violations_after_previous_activity':
      return `В прошлом периоде ${countRu(facts.previous_violations_count ?? 0, VIOLATION_FORMS)} · ${num1(facts.available_control_hours)} ч контроля`;
    case 'cameras_without_violations': {
      const ids = Array.isArray(facts.camera_ids) ? facts.camera_ids : [];
      const names = ids.map((id) => cameraNames[id]).filter(Boolean);
      const shown = names.slice(0, 5).join(', ');
      const rest = names.length > 5 ? ` и ещё ${names.length - 5}` : '';
      return `${countRu(facts.camera_count ?? ids.length, CAMERA_FORMS)}${shown ? ` — ${shown}${rest}` : ''}`;
    }
    default:
      return '';
  }
};

const trendFormatter = (tz, withTime) => {
  const opts = { day: '2-digit', month: '2-digit', hour12: false, timeZone: tz || 'UTC' };
  if (withTime) Object.assign(opts, { hour: '2-digit', minute: '2-digit' });
  try {
    return new Intl.DateTimeFormat('ru-RU', opts);
  } catch (e) {
    return new Intl.DateTimeFormat('ru-RU', { ...opts, timeZone: 'UTC' });
  }
};

/** Подписи тренда — по scope.trend_timezone, не по timezone браузера (дополнение §2.1). */
export const formatTrendTick = (iso, bucket, tz) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return trendFormatter(tz, bucket === 'hour').format(date).replace(',', '');
};

export const formatTrendRange = (fromIso, toIso, tz) =>
  `${formatTrendTick(fromIso, 'hour', tz)} – ${formatTrendTick(toIso, 'hour', tz)}`;

/**
 * ТЗ №1 §10.2 + FE-FIX09 + ТЗ №11. Параметры совпадают с URL-схемой вкладки событий
 * (factory/workshop/section/room/camera/rule/from/to). zone_id не передаётся — Events API его не принимает.
 *
 * from/to кладём ровно так, как их прислал backend: округление до минут
 * сдвигало границы интервала, и открытый список показывал не те события,
 * по которым посчитан инсайт. camera_id и rule_id по контракту nullable —
 * сводный паттерн без камеры это нормальный drilldown, а не повод прятать CTA.
 */
const HIERARCHY_DRILLDOWN_PARAMS = [
  ['factory_id', 'factory'],
  ['workshop_id', 'workshop'],
  ['section_id', 'section'],
  ['room_id', 'room'],
  ['camera_id', 'camera'],
];

export const buildEventsDrilldownUrl = (drilldown) => {
  if (!drilldown?.from && !drilldown?.to) return null;

  const params = new URLSearchParams();
  // Имена параметров — как в URL-схеме вкладки событий (HIERARCHY_URL_SCHEMA).
  HIERARCHY_DRILLDOWN_PARAMS.forEach(([field, param]) => {
    if (drilldown[field] != null) params.set(param, String(drilldown[field]));
  });
  if (drilldown.rule_id) params.set('rule', drilldown.rule_id);
  if (drilldown.from) params.set('from', drilldown.from);
  if (drilldown.to) params.set('to', drilldown.to);

  return `/violations?${params.toString()}`;
};

/**
 * BM-FE-TZ-002C: drill-down из карточки конкретного правила — события этого
 * rule_id и этой камеры за весь текущий период вкладки, а не за окно
 * backend-drilldown (оно короче периода). Без правила drill-down нет: по
 * нескольким правилам список событий фильтровать не умеет.
 */
export const buildRuleDrilldown = (pattern, period) => {
  if (!pattern?.rule_id) return null;
  // Без периода вкладки (блок вне страницы) — окно, которое дал backend.
  const from = period?.from || pattern.drilldown?.from;
  const to = period?.to || pattern.drilldown?.to;
  if (!from || !to) return null;
  return { rule_id: pattern.rule_id, camera_id: pattern.camera_id ?? undefined, from, to };
};

/**
 * FE-FIX13. «К предыдущему периоду» не говорило, какой это период.
 *
 * Интервал сравнения берётся из scope.comparison_from/comparison_to, а не
 * вычисляется по формуле: backend считает дельты KPI именно по нему
 * (comparison_time_basis = calendar_aligned_previous_period), и собственный
 * расчёт фронта мог бы разойтись с цифрами, которые стоят рядом.
 *
 * comparison_state != 'available' — интервала нет совсем: показать интервал,
 * по которому сравнение не проводилось, хуже, чем не показать ничего.
 *
 * FE-FIX20 (SUMMARY-COMPARISON-STATE02). comparison_state = no_prior_history
 * означает, что более ранней истории в этом scope нет вообще, — и это не то же
 * самое, что «в прошлом периоде было 0 нарушений». Различие приходит от
 * backend: previous_violations_count = 0 при comparison_state = available
 * остаётся обычным сравнением с нулём и никакой особой подписи не получает.
 */
export const NO_PRIOR_HISTORY_TEXT = 'Более ранних событий ещё не было';

export const describeComparisonPeriods = (scope) => {
  if (!scope) return { current: '', previous: '', previousNote: '' };

  const tz = scope.display_timezone;
  const current = scope.from && scope.to ? formatTrendRange(scope.from, scope.to, tz) : '';

  const hasComparison = scope.comparison_state === 'available'
    && scope.comparison_from
    && scope.comparison_to;

  return {
    current,
    previous: hasComparison ? formatTrendRange(scope.comparison_from, scope.comparison_to, tz) : '',
    previousNote: scope.comparison_state === 'no_prior_history' ? NO_PRIOR_HISTORY_TEXT : '',
  };
};

/*
 * Сравнение с прошлым периодом в ячейках таблиц и строках распределений.
 * previous = null — сравнения нет («—»), это не ноль. previous = 0 даёт
 * «было 0», но без процента: делить не на что.
 */

/**
 * Относительное изменение со знаком U+2212 и цветом по смыслу метрики.
 * good = 'down' — рост плохой (нарушения, на 100 ч), good = 'up' — рост хороший
 * (часы контроля, непрерывность).
 */
export const describeDelta = (current, previous, good = 'down') => {
  if (current == null || previous == null) return null;
  const cur = Number(current);
  const prev = Number(previous);
  if (!Number.isFinite(cur) || !Number.isFinite(prev) || prev === 0) return null;
  const percent = Math.round(((cur - prev) / Math.abs(prev)) * 100);
  if (percent === 0) return { text: '0%', tone: 'same' };
  const rose = percent > 0;
  // BM v2 §2.1: красный — только там, где рост объективно плох. Для
  // бизнес-событий направление нейтрально: знак есть, цвета оценки нет.
  if (good === 'none') return { text: `${rose ? '+' : '−'}${Math.abs(percent)}%`, tone: 'same' };
  const improved = good === 'down' ? !rose : rose;
  return { text: `${rose ? '+' : '−'}${Math.abs(percent)}%`, tone: improved ? 'good' : 'bad' };
};

/** Целое число с пробелом-разделителем тысяч: 1 052. */
export const formatCountCell = (value) => (
  value == null || !Number.isFinite(Number(value)) ? '—' : Number(value).toLocaleString('ru-RU')
);

/** Точки sparkline: текущий и прошлый ряд по номеру точки, пропуски остаются null. */
export const buildSparklineData = (points, previousPoints) => {
  const current = Array.isArray(points) ? points : [];
  const previous = Array.isArray(previousPoints) ? previousPoints : [];
  const size = Math.max(current.length, previous.length);
  return Array.from({ length: size }, (_, index) => ({
    current: current[index]?.violations_count ?? null,
    previous: previous[index]?.violations_count ?? null,
  }));
};
